import { unsafeCSS, html } from "lit";
import { property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { LeafElement } from "../leaf-element.js";
import { appendStylesheet } from "../../utilities/light-dom-styles.js";
import { isMobile } from "../../utilities/is-mobile.js";
import { watch } from "../../utilities/watch.js";
import "../../utilities/events.js";
const styles = `/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

/* ------------------------------------*\\
    #TYPOGRAPHY PLACEHOLDERS
\\*------------------------------------ */
.leaf-c-content-card ::slotted([slot=content]) {
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
          text-decoration: var(--leaf-typography-body-default-text-decoration);
  text-transform: var(--leaf-typography-body-default-text-transform);
}

.leaf-c-content-card ::slotted([slot=meta]) {
  font-family: var(--leaf-typography-body-small-font-family);
  font-size: var(--leaf-typography-body-small-font-size);
  font-weight: var(--leaf-typography-body-small-font-weight);
  line-height: var(--leaf-typography-body-small-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-small-text-decoration);
          text-decoration: var(--leaf-typography-body-small-text-decoration);
  text-transform: var(--leaf-typography-body-small-text-transform);
}

.leaf-c-content-card [slot=heading] a {
  font-family: unset;
  font-size: unset;
  font-weight: unset;
  line-height: unset;
  -webkit-text-decoration: unset;
  text-decoration: unset;
  text-transform: unset;
  border-bottom: unset;
}

:host {
  --tcg-card-box-shadow-color: var(--leaf-color-border-default);
  display: block;
}

.leaf-c-content-card {
  color: var(--leaf-color-content-default);
  height: 100%;
}
.leaf-c-content-card ::slotted([slot=meta]),
.leaf-c-content-card [slot=heading],
.leaf-c-content-card ::slotted([slot=content]) {
  margin-block: 0 !important;
  padding-bottom: var(--leaf-spacing-4);
}
.leaf-c-content-card [slot=heading] :focus-visible {
  box-shadow: none;
}
.leaf-c-content-card [slot=heading] a {
  font-family: var(--leaf-typography-body-default-link-font-family);
  font-size: inherit;
  font-weight: var(--leaf-typography-body-default-link-font-weight);
  line-height: inherit;
  -webkit-text-decoration: var(--leaf-text-decoration-none);
          text-decoration: var(--leaf-text-decoration-none);
  text-transform: var(--leaf-typography-body-default-link-text-transform);
  border-bottom: var(--leaf-border-width-default) solid;
  color: var(--leaf-color-content-link);
  border-bottom: 0;
}
.leaf-c-content-card [slot=heading] a:hover {
  color: var(--leaf-color-content-link-hover);
}
.leaf-c-content-card [slot=heading] a:focus {
  background-color: var(--leaf-color-bg-knockout);
  border-color: var(--leafdcws-color-border-transparent);
  color: var(--leaf-color-content-knockout);
  outline: none;
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--external)::after {
  background-color: currentColor;
  content: "";
  margin: 0 0 0 var(--leaf-spacing-8);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--inverted) {
  color: var(--leaf-color-content-knockout);
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--inverted):hover {
  color: var(--leaf-color-content-brand-knockout);
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--inverted):focus {
  background-color: var(--leaf-color-bg-default);
  color: var(--leaf-color-content-default);
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action) {
  display: inline-block;
  margin: var(--leaf-spacing-8) calc(var(--leaf-spacing-48) - var(--leaf-spacing-4));
  margin-left: 0;
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action)::after {
  background-color: currentColor;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3C/svg%3E");
  height: var(--leaf-spacing-40);
  margin-left: var(--leaf-spacing-4);
  position: absolute;
  transform: translateY(calc(var(--leaf-spacing-8) * -1));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  width: var(--leaf-spacing-40);
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action):focus:after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20.2803 12L18.8703 13.41L24.4503 19H12.2803V21H24.4503L18.8703 26.59L20.2803 28L28.2803 20L20.2803 12Z' fill='white'/%3E%3Crect x='39.2803' y='39' width='38' height='38' rx='19' transform='rotate(180 39.2803 39)' stroke='black' stroke-width='2'/%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action):where(.leaf-u-link--inverted)::after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3C/svg%3E");
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action):where(.leaf-u-link--inverted):hover::after {
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
}
.leaf-c-content-card [slot=heading] a:where(.leaf-u-link--action):where(.leaf-u-link--inverted):focus:after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='white' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
.leaf-c-content-card [slot=heading],
.leaf-c-content-card [slot=heading] a {
  font-family: var(--leaf-typography-title-default-font-family) !important;
  font-size: var(--leaf-typography-label-large-font-size) !important;
  font-weight: var(--leaf-typography-title-default-font-weight) !important;
  line-height: var(--leaf-typography-title-default-line-height) !important;
  -webkit-text-decoration: var(--leaf-typography-title-default-text-decoration) !important;
          text-decoration: var(--leaf-typography-title-default-text-decoration) !important;
  text-transform: var(--leaf-typography-title-default-text-transform) !important;
}
.leaf-c-content-card [slot=heading] {
  color: var(--leaf-color-content-brand-strong);
}
.leaf-c-content-card .inner-wrapper {
  border-radius: var(--leaf-border-radius-large);
  background-color: var(--leaf-color-bg-default);
  box-shadow: 0 0 0 var(--leaf-border-width-focus) var(--tcg-card-box-shadow-color);
  height: 100%;
  width: 100%;
}

.leaf-c-content-card--simple ::slotted([slot=decoration]) {
  --icon-height: 64px;
  --icon-width: 64px;
  --icon-fill: var(--leaf-color-content-brand);
  display: block;
  margin-bottom: var(--leaf-spacing-24);
}
.leaf-c-content-card--simple.leaf-c-content-card--decoration-center ::slotted([slot=decoration]) {
  width: 100%;
}
.leaf-c-content-card--simple.leaf-c-content-card--text-center .card-body {
  text-align: center;
}
.leaf-c-content-card--simple .inner-wrapper {
  padding: var(--leaf-spacing-24);
}

.leaf-c-content-card--image .inner-wrapper {
  display: flex;
  flex-direction: column;
}
.leaf-c-content-card--image ::slotted([slot=decoration]) {
  width: 100%;
}
.leaf-c-content-card--image .card-body {
  padding: var(--leaf-spacing-24);
}
@media (min-width: 60rem) {
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) .inner-wrapper {
    flex-direction: row;
  }
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) ::slotted([slot=decoration]) {
    display: none !important;
  }
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) .card-decoration {
    background-size: cover;
    background-position: center;
    border-radius: var(--leaf-border-radius-large) 0 0 var(--leaf-border-radius-large);
    min-height: 350px;
    width: 50%;
  }
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) .card-body {
    margin: auto 0;
    width: 50%;
  }
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) [slot=heading],
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) [slot=heading] a {
    padding: unset;
    font-family: var(--leaf-typography-title-large-font-family) !important;
    font-size: var(--leaf-typography-title-default-font-size) !important;
    font-weight: var(--leaf-typography-title-large-font-weight) !important;
    line-height: var(--leaf-typography-title-large-line-height) !important;
    -webkit-text-decoration: var(--leaf-typography-title-large-text-decoration) !important;
            text-decoration: var(--leaf-typography-title-large-text-decoration) !important;
    text-transform: var(--leaf-typography-title-large-text-transform) !important;
  }
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) ::slotted([slot=content]),
  .leaf-c-content-card--image:not(.leaf-c-content-card--decoration-center) ::slotted([slot=cta]) {
    padding: var(--leaf-spacing-8) 0;
  }
}
.leaf-c-content-card--image.leaf-c-content-card--inverted .inner-wrapper {
  --tcg-card-box-shadow-color: var(--leaf-color-border-default);
  background-color: var(--leaf-color-bg-brand-xstrong);
  color: var(--leaf-color-content-knockout);
}
.leaf-c-content-card--image.leaf-c-content-card--inverted [slot=heading] {
  color: var(--leaf-color-content-knockout);
}
.leaf-c-content-card--image.leaf-c-content-card--inverted [slot=heading] a {
  color: var(--leaf-color-content-knockout);
}
.leaf-c-content-card--image.leaf-c-content-card--inverted [slot=heading] a:focus {
  background-color: var(--leaf-color-bg-default);
  color: var(--leaf-color-content-default);
}

.leaf-c-content-card--interactive {
  overflow: visible;
}
.leaf-c-content-card--interactive [slot=heading] {
  color: var(--leaf-color-content-brand);
}
.leaf-c-content-card--interactive .inner-wrapper {
  background-color: var(--leaf-color-bg-default);
  border-radius: var(--leaf-border-radius-large);
  cursor: pointer;
  position: relative;
  overflow: visible;
}
.leaf-c-content-card--interactive .inner-wrapper:hover {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand-hover);
}
.leaf-c-content-card--interactive .inner-wrapper.no-touch {
  --tcg-card-box-shadow-color: var(--leaf-color-border-default);
  background-color: var(--leaf-color-bg-default);
}
.leaf-c-content-card--interactive .inner-wrapper:focus-within {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand-hover);
}
.leaf-c-content-card--interactive .inner-wrapper:active, .leaf-c-content-card--interactive .inner-wrapper:focus {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper {
  background-color: var(--leaf-color-bg-brand-xstrong);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper:hover {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand-xstrong-hover);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper.no-touch {
  --tcg-card-box-shadow-color: var(--leaf-color-border-default);
  background-color: var(--leaf-color-bg-brand-xstrong);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper:focus-within {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand-xstrong-hover);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper:active, .leaf-c-content-card--interactive.leaf-c-content-card--inverted .inner-wrapper:focus {
  --tcg-card-box-shadow-color: var(--leaf-color-border-brand);
  background-color: var(--leaf-color-bg-brand-xstrong-active);
}
.leaf-c-content-card--interactive.leaf-c-content-card--inverted [slot=heading] a:hover, .leaf-c-content-card--interactive.leaf-c-content-card--inverted [slot=heading] a:focus, .leaf-c-content-card--interactive.leaf-c-content-card--inverted [slot=heading] a:active {
  --tcg-card-box-shadow-color: var(--leaf-color-border-transparent);
  background-color: unset;
  color: var(--leaf-color-content-knockout);
}
.leaf-c-content-card--interactive [slot=heading] a::before {
  content: "";
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}
.leaf-c-content-card--interactive [slot=heading] a:hover, .leaf-c-content-card--interactive [slot=heading] a:focus, .leaf-c-content-card--interactive [slot=heading] a:active {
  --tcg-card-box-shadow-color: var(--leaf-color-border-transparent);
  background-color: unset;
  color: unset;
}
.leaf-c-content-card--interactive [slot=heading] a:focus-visible::before {
  border-radius: var(--leaf-border-radius-large);
  box-shadow: 0 0 0 var(--leaf-border-width-focus) var(--leaf-color-border-brand), 0 0 0 calc(var(--leaf-border-width-focus) * 2) var(--leaf-color-border-knockout), 0 0 0 calc(var(--leaf-border-width-focus) * 3) var(--leaf-color-border-active-outline);
}`;
const lightDomStyles = "leaf-card img {\n  border-radius: var(--leaf-border-radius-large) var(--leaf-border-radius-large) 0 0;\n  width: 100%;\n  -o-object-fit: cover;\n     object-fit: cover;\n}\n@media (min-width: 60rem) {\n  leaf-card:not([decorationposition=center]) img {\n    border-radius: var(--leaf-border-radius-large) 0 0 var(--leaf-border-radius-large);\n  }\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
class LeafCard extends LeafElement {
  constructor() {
    super();
    this.variant = "simple";
    this.decorationPosition = "left";
    this.textPosition = "left";
    this.interactive = false;
    this.inverted = false;
    this.setSrc = (currentSrc, label) => {
      var _a, _b, _c;
      let style = `background-image: url(${currentSrc});`;
      if (this.imagePosition) {
        style += `background-position: ${this.imagePosition};`;
      }
      (_a = this.cardDecoration) == null ? void 0 : _a.setAttribute("style", style);
      if (label) {
        (_b = this.cardDecoration) == null ? void 0 : _b.setAttribute("role", "img");
        (_c = this.cardDecoration) == null ? void 0 : _c.setAttribute("aria-label", `${label}`);
      }
    };
    this.setImage = () => {
      var _a;
      if (!isMobile("lg") && this.decorationPosition === "left" && this.slottedImage) {
        const slottedElement = this.slottedImage.assignedNodes()[0];
        const image = slottedElement.nodeName === "PICTURE" ? slottedElement.querySelector("img") : slottedElement;
        const label = image == null ? void 0 : image.getAttribute("aria-label");
        if (image == null ? void 0 : image.complete) {
          this.setSrc(image == null ? void 0 : image.currentSrc, label);
        } else {
          if (image) {
            image.onload = () => {
              this.setSrc(image == null ? void 0 : image.currentSrc, label);
            };
          }
        }
      } else {
        (_a = this.cardDecoration) == null ? void 0 : _a.removeAttribute("style");
      }
    };
    appendStylesheet("leaf-card-styles", lightDomStyles);
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", () => {
      this.handleDecorationChange();
    });
    if (this.interactive && isMobile()) {
      this.addEventListener("touchstart", () => this.cardInnerWrapper.classList.remove("no-touch"));
      this.addEventListener("touchend", () => this.cardInnerWrapper.classList.add("no-touch"));
    }
  }
  handleHeadingSlotChange(event) {
    const slotElements = event.target.assignedElements();
    const heading = slotElements[0];
    if (event.target && heading) {
      event.target.insertAdjacentElement("beforebegin", heading);
    }
  }
  handleDecorationChange() {
    if (this.variant === "image") {
      this.setImage();
    }
  }
  updateImagePosition() {
    this.handleDecorationChange();
  }
  render() {
    const componentClassName = {
      "leaf-c-content-card": true,
      "leaf-c-content-card--interactive": this.interactive,
      "leaf-c-content-card--decoration-center": this.decorationPosition === "center",
      "leaf-c-content-card--text-center": this.textPosition === "center",
      "leaf-c-content-card--simple": this.variant === "simple",
      "leaf-c-content-card--image": this.variant === "image",
      "leaf-c-content-card--inverted": this.inverted
    };
    return html`
      <div class="${classMap(componentClassName)}">
        <div class="inner-wrapper">
          <div class="card-decoration">
            <slot name="decoration" @slotchange=${this.handleDecorationChange}></slot>
          </div>
          <div class="card-body">
            <slot name="meta"></slot>
            <slot name="heading" @slotchange=${this.handleHeadingSlotChange}></slot>
            <slot name="content"></slot>
            <slot name="cta"></slot>
          </div>
        </div>
      </div>
    `;
  }
}
LeafCard.styles = unsafeCSS(styles);
__decorateClass([
  property()
], LeafCard.prototype, "variant", 2);
__decorateClass([
  property()
], LeafCard.prototype, "decorationPosition", 2);
__decorateClass([
  property()
], LeafCard.prototype, "textPosition", 2);
__decorateClass([
  property({ type: Boolean })
], LeafCard.prototype, "interactive", 2);
__decorateClass([
  property({ type: Boolean })
], LeafCard.prototype, "inverted", 2);
__decorateClass([
  property({ attribute: "image-position" })
], LeafCard.prototype, "imagePosition", 2);
__decorateClass([
  query('slot[name="decoration"]')
], LeafCard.prototype, "slottedImage", 2);
__decorateClass([
  query(".card-decoration")
], LeafCard.prototype, "cardDecoration", 2);
__decorateClass([
  query(".inner-wrapper")
], LeafCard.prototype, "cardInnerWrapper", 2);
__decorateClass([
  watch(["imagePosition", "decorationPosition"])
], LeafCard.prototype, "updateImagePosition", 1);
export {
  LeafCard
};
//# sourceMappingURL=card.component.js.map
